import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Form,
    Input,
    Label,
    Row,
    Button,
    Table,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    FormGroup,
} from "reactstrap";
import { getGiftCards, saveGiftCard } from "../../components/Api/GmsGiftCard";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
    hasPagePermissionWithAdmin,
    hasViewOnlyPermission,
    hasEditOrHigherPermission,
    hasDeletePermissionWithAdmin,
    isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '38px',
        borderColor: state.isFocused ? '#556ee6' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        maxHeight: 200,
    }),
    menuList: (provided) => ({
        ...provided,
        maxHeight: 180,
        overflowY: 'auto',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
        color: state.isSelected ? 'white' : '#495057',
        '&:hover': {
            backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
        },
    }),
};

// Session management
const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
        userId: authUser.userId || user.user_Id || 1,
        userName: authUser.userName || user.user_NameEn || "System",
        companyId: authUser.companyId || user.user_Company_Id || null,
        branchId: authUser.branchId || user.user_Branch_Id || null,
    };
};

const defaultForm = {
    Card_Id: 0,
    Business_Id: null,
    Branch_Id: null,
    Card_Name: "",
    Amount: 0,
    Status: true,
    Created_By: 1,
    Created_On: new Date().toISOString(),
};

const emptyFilter = {
    Card_Id: null,
    Business_Id: null,
    Branch_Id: null,
    Status: null,
};

export default function GiftCardPage() {
    const [giftCards, setGiftCards] = useState([]);
    const [form, setForm] = useState(defaultForm);
    const [filter, setFilter] = useState(emptyFilter);
    const [businessOptions, setBusinessOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [filterBranchOptions, setFilterBranchOptions] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [editLoadingRow, setEditLoadingRow] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [activeTab, setActiveTab] = useState("1");
    const [showFilters, setShowFilters] = useState(false);

    // Check permissions
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const pageRoute = '/GmsGiftCard';
    const hasPermission = hasPagePermissionWithAdmin(permissions, pageRoute);
    const isViewOnly = hasViewOnlyPermission(permissions, pageRoute);
    const hasEditOrHigher = hasEditOrHigherPermission(permissions, pageRoute);
    const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, pageRoute);

    useEffect(() => {
        if (hasPermission) {
            loadInitialData();
            loadGiftCards();
            if (isViewOnly) {
                setActiveTab("2");
            }
        } else {
            // Redirect handled by router or useEffect in common pattern
            // but adding a safety check
        }
    }, [hasPermission]);

    const loadInitialData = async () => {
        try {
            const businesses = await getBusinesses({ business_Active: true });
            const businessOpts = businesses.map(b => ({
                value: b.business_Id,
                label: b.business_NameEn || b.business_Name
            }));
            setBusinessOptions(businessOpts);

            const branches = await getBranches({ branch_Active: true });
            const branchOpts = branches.map(b => ({
                value: b.branch_Id,
                label: b.branch_NameEn || b.branch_Name,
                businessId: b.branch_Buisness_Id || b.branch_BusinessId
            }));
            setBranchOptions(branchOpts);
        } catch (error) {
            console.error("Error loading initial data:", error);
        }
    };


    const loadGiftCards = async (filterData = filter) => {
        setLoading(true);
        try {
            const data = await getGiftCards(filterData);
            const mappedData = Array.isArray(data) ? data.map(item => ({
                Card_Id: item.card_Id || item.Card_Id,
                Business_Id: item.business_Id || item.Business_Id,
                Branch_Id: item.branch_Id || item.Branch_Id,
                Card_Name: item.card_Name || item.Card_Name,
                Amount: item.amount || item.Amount,
                Status: item.status !== undefined ? item.status : item.Status,
                Created_By: item.created_By || item.Created_By,
                Created_On: item.created_On || item.Created_On,
                Modified_By: item.modified_By || item.Modified_By,
                Modified_On: item.modified_On || item.Modified_On,
                BusinessName: item.businessName || item.BusinessName,
                BranchName: item.branchName || item.BranchName,
                CreatedByName: item.createdByName || item.CreatedByName,
                ModifiedByName: item.modifiedByName || item.ModifiedByName,
            })) : [];
            setGiftCards(mappedData);
            setTotalCount(mappedData.length);
        } catch (error) {
            Swal.fire("Error", "Failed to load gift cards: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleBusinessChange = (selectedOption) => {
        const businessId = selectedOption ? selectedOption.value : null;
        setForm(prev => ({ ...prev, Business_Id: businessId, Branch_Id: null }));
    };

    const handleBranchChange = (selectedOption) => {
        setForm(prev => ({ ...prev, Branch_Id: selectedOption ? selectedOption.value : null }));
    };

    const handleFilterBusinessChange = (selectedOption) => {
        const businessId = selectedOption ? selectedOption.value : null;
        setFilter(prev => ({ ...prev, Business_Id: businessId, Branch_Id: null }));
        if (businessId) {
            setFilterBranchOptions(branchOptions.filter(b => b.businessId === businessId));
        } else {
            setFilterBranchOptions([]);
        }
    };

    const handleFormChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.Business_Id || !form.Branch_Id || !form.Card_Name || form.Amount <= 0) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        setSaveLoading(true);
        try {
            const session = getUserSession();
            const giftCardData = {
                ...form,
                Created_By: isEditing ? form.Created_By : session.userId,
                Modified_By: session.userId,
            };

            const result = await saveGiftCard(giftCardData);
            const isSuccess = result.IsSuccess || result.isSuccess;
            const message = result.Message || result.message;

            if (isSuccess) {
                Swal.fire("Success", message || "Gift card saved successfully!", "success");
                handleClear();
                loadGiftCards();
                setActiveTab("2");
            } else {
                Swal.fire("Error", message || "Failed to save gift card", "error");
            }
        } catch (error) {
            Swal.fire("Error", error.message || "Failed to save gift card", "error");
        } finally {

            setSaveLoading(false);
        }
    };

    const handleEdit = (card) => {
        setEditLoadingRow(card.Card_Id);
        try {
            setForm({
                Card_Id: card.Card_Id,
                Business_Id: card.Business_Id,
                Branch_Id: card.Branch_Id,
                Card_Name: card.Card_Name,
                Amount: card.Amount,
                Status: card.Status,
                Created_By: card.Created_By,
                Created_On: card.Created_On,
            });
            setIsEditing(true);
            setActiveTab("1");
            window.scrollTo(0, 0);
        } catch (error) {
            Swal.fire("Error", "Failed to load gift card data", "error");
        } finally {
            setEditLoadingRow(null);
        }
    };

    const handleClear = () => {
        setForm(defaultForm);
        setIsEditing(false);
    };

    const handleApplyFilter = () => {
        setPage(1);
        loadGiftCards(filter);
    };

    const handleClearFilter = () => {
        setFilter(emptyFilter);
        setFilterBranchOptions([]);
        setPage(1);
        loadGiftCards(emptyFilter);
    };

    const getPaginatedData = () => {
        const startIndex = (page - 1) * pageSize;
        return giftCards.slice(startIndex, startIndex + pageSize);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const date = new Date(dateStr);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!hasPermission) {
        return <div className="page-content"><Container fluid><h4>Access Denied</h4></Container></div>;
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Finance Setup" breadcrumbItem="Gift Cards" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody className="p-0">
                                <Nav tabs className="nav-tabs-custom">
                                    {!isViewOnly && (
                                        <NavItem>
                                            <NavLink
                                                style={{
                                                    cursor: "pointer",
                                                    fontWeight: activeTab === "1" ? "bold" : "normal",
                                                    color: activeTab === "1" ? "#556ee6" : "#74788d",
                                                    borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                                                    padding: "15px 25px",
                                                    fontSize: "16px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px"
                                                }}
                                                className={activeTab === "1" ? "active" : ""}
                                                onClick={() => setActiveTab("1")}
                                            >
                                                <i className="bx bx-plus-circle"></i>
                                                <span className="d-none d-sm-inline-block">Gift Card Form</span>
                                            </NavLink>
                                        </NavItem>
                                    )}
                                    <NavItem>
                                        <NavLink
                                            style={{
                                                cursor: "pointer",
                                                fontWeight: activeTab === "2" ? "bold" : "normal",
                                                color: activeTab === "2" ? "#556ee6" : "#74788d",
                                                borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                                                padding: "15px 25px",
                                                fontSize: "16px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px"
                                            }}
                                            className={activeTab === "2" ? "active" : ""}
                                            onClick={() => setActiveTab("2")}
                                        >
                                            <i className="bx bx-list-ul"></i>
                                            <span className="d-none d-sm-inline-block">Gift Card List</span>
                                        </NavLink>
                                    </NavItem>
                                </Nav>


                                <TabContent activeTab={activeTab} className="p-4">
                                    <TabPane tabId="1">
                                        <Form onSubmit={handleSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup className="mb-4">
                                                        <Label>Company <span className="text-danger">*</span></Label>
                                                        <Select
                                                            options={businessOptions}
                                                            value={businessOptions.find(o => o.value === form.Business_Id)}
                                                            onChange={handleBusinessChange}
                                                            styles={selectStyles}
                                                            placeholder="Select Company"
                                                            required
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup className="mb-4">
                                                        <Label>Branch <span className="text-danger">*</span></Label>
                                                        <Select
                                                            options={branchOptions.filter(b => b.businessId === form.Business_Id)}
                                                            value={branchOptions.find(o => o.value === form.Branch_Id)}
                                                            onChange={handleBranchChange}
                                                            styles={selectStyles}
                                                            placeholder="Select Branch"
                                                            isDisabled={!form.Business_Id}
                                                            required
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup className="mb-4">
                                                        <Label>Gift Card Name <span className="text-danger">*</span></Label>
                                                        <Input
                                                            type="text"
                                                            value={form.Card_Name}
                                                            onChange={(e) => handleFormChange("Card_Name", e.target.value)}
                                                            placeholder="Enter Card Name"
                                                            required
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup className="mb-4">
                                                        <Label>Amount <span className="text-danger">*</span></Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={form.Amount}
                                                            onChange={(e) => handleFormChange("Amount", parseFloat(e.target.value))}
                                                            placeholder="0.00"
                                                            required
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup className="mb-4">
                                                        <Label>Status</Label>
                                                        <div className="form-check form-switch form-switch-md">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id="statusSwitch"
                                                                checked={form.Status}
                                                                onChange={(e) => handleFormChange("Status", e.target.checked)}
                                                                disabled={!hasDeletePermission && isEditing}
                                                            />
                                                            <label className="form-check-label" htmlFor="statusSwitch">
                                                                {form.Status ? "Active" : "Inactive"}
                                                            </label>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <div className="d-flex gap-2 mt-4">
                                                <Button color="primary" type="submit" disabled={saveLoading || (isEditing && !hasEditOrHigher)}>
                                                    {saveLoading ? <ClipLoader size={16} color="#fff" /> : (isEditing ? "Update" : "Save")}
                                                </Button>
                                                <Button color="secondary" type="button" onClick={handleClear}>
                                                    Clear
                                                </Button>
                                            </div>
                                        </Form>
                                    </TabPane>

                                    <TabPane tabId="2">
                                        <div className="mb-3">
                                            <Button
                                                color="light"
                                                onClick={() => setShowFilters(!showFilters)}
                                                className="mb-3 px-3"
                                            >
                                                {showFilters ? "Hide Filters" : "Show Filters"} <i className={`bx bx-chevron-${showFilters ? 'up' : 'down'}`}></i>
                                            </Button>

                                            {showFilters && (
                                                <Card className="border shadow-none mb-3">
                                                    <CardBody>
                                                        <Row>
                                                            <Col md={4}>
                                                                <FormGroup>
                                                                    <Label>Company</Label>
                                                                    <Select
                                                                        options={businessOptions}
                                                                        value={businessOptions.find(o => o.value === filter.Business_Id)}
                                                                        onChange={handleFilterBusinessChange}
                                                                        styles={selectStyles}
                                                                        isClearable
                                                                    />
                                                                </FormGroup>
                                                            </Col>
                                                            <Col md={4}>
                                                                <FormGroup>
                                                                    <Label>Branch</Label>
                                                                    <Select
                                                                        options={filterBranchOptions}
                                                                        value={branchOptions.find(o => o.value === filter.Branch_Id)}
                                                                        onChange={(opt) => setFilter(f => ({ ...f, Branch_Id: opt ? opt.value : null }))}
                                                                        styles={selectStyles}
                                                                        isClearable
                                                                    />
                                                                </FormGroup>
                                                            </Col>
                                                            <Col md={4} className="d-flex align-items-end mb-3">
                                                                <Button color="primary" onClick={handleApplyFilter} className="me-2">Apply</Button>
                                                                <Button color="secondary" onClick={handleClearFilter}>Clear</Button>
                                                            </Col>
                                                        </Row>
                                                    </CardBody>
                                                </Card>
                                            )}

                                            <div className="table-responsive">
                                                <Table bordered hover className="align-middle">
                                                    <thead className="table-light text-center">
                                                        <tr className="text-uppercase font-size-12">
                                                            <th style={{ width: "80px" }}>ID</th>
                                                            <th className="text-start">Company</th>
                                                            <th className="text-start">Branch</th>
                                                            <th className="text-start">Card Name</th>
                                                            <th className="text-end">Amount</th>
                                                            <th>Status</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="text-center">
                                                        {loading ? (
                                                            <tr><td colSpan="7" className="text-center py-4"><ClipLoader size={30} /></td></tr>
                                                        ) : getPaginatedData().length === 0 ? (
                                                            <tr><td colSpan="7" className="text-center py-4">No data found</td></tr>
                                                        ) : (
                                                            getPaginatedData().map((card) => (
                                                                <tr key={card.Card_Id}>
                                                                    <td>{card.Card_Id}</td>
                                                                    <td className="text-start">{card.BusinessName}</td>
                                                                    <td className="text-start">{card.BranchName}</td>
                                                                    <td className="text-start">{card.Card_Name}</td>
                                                                    <td className="text-end font-weight-bold">{card.Amount.toFixed(2)}</td>
                                                                    <td>
                                                                        <span className={`badge badge-pill badge-soft-${card.Status ? 'success' : 'danger'} font-size-11 p-2`}>
                                                                            {card.Status ? 'Active' : 'Inactive'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <Button
                                                                            color="primary"
                                                                            size="sm"
                                                                            className="btn-soft-primary"
                                                                            onClick={() => handleEdit(card)}
                                                                            disabled={editLoadingRow === card.Card_Id || !hasEditOrHigher}
                                                                        >
                                                                            <i className="bx bx-edit-alt"></i> Edit
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            <div className="d-flex justify-content-between mt-3">
                                                <div>Showing {getPaginatedData().length} of {totalCount} entries</div>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        color="light"
                                                        disabled={page === 1}
                                                        onClick={() => setPage(page - 1)}
                                                    >Previous</Button>
                                                    <Button
                                                        color="light"
                                                        disabled={page * pageSize >= totalCount}
                                                        onClick={() => setPage(page + 1)}
                                                    >Next</Button>
                                                </div>
                                            </div>
                                        </div>
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}
